import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpBorService from "@plannel/services/fp/fp-bor-service";
import RoutePopup from "@plannel/pages/factory-plan/common/popup/RoutePopup";
import ResourcePopup from "@plannel/pages/factory-plan/common/popup/ResourcePopup";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { useCommonCodeValues, codeComboCellEditorParams, findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, DownloadButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// old validRules required 2개(routeCd/resourceCd) — Stock.js §REQUIRED_FIELDS 패턴.
const REQUIRED_FIELDS = [
  { field: "routeCd", label: "FP_ROUTE_CD" },
  { field: "resourceCd", label: "FP_RESOURCE_CD" }
];

/**
 * BOR(공정별 사용 자원) 탭 — old BorTab.jsx 대응. §11-1(항상 마운트 + display 토글)로 Bor.js에서
 * 렌더링되므로, planScope/itemCdNm이 바뀔 때마다 자체적으로 재조회한다(routeCdNm/resourceCdNm은
 * 클라이언트 필터, §6-1 케이스3 — itemCdNm만 예외적으로 서버 재조회, fp-master-screen-guide.md §6-1
 * 예외표). searchFields는 Bor.js가 소유한 전체 필드 중 이 탭이 쓰는 부분만 사용.
 *
 * props: t, viewName, planScope, searchFields
 */
const BorTab = ({ t, viewName, planScope, searchFields }) => {
  const gridRef = useRef();
  const [bor, setBor] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  // old는 routePopupOpen/routePopupType 하나로 routeCd(선택)/routeProdItems(조회) 두 버튼을
  // 같은 RoutePopup 인스턴스로 열었다 — new도 old와 동일한 type 이름/값으로 분기해 인스턴스 하나만 둔다.
  const [routePopup, setRoutePopup] = useState({ open: false, type: "routeCd", node: null, routeCd: null });
  const [resourcePopup, setResourcePopup] = useState({ open: false, node: null });

  const timeUomValuesRef = useCommonCodeValues("FP_TIME_UOM");
  const processTimeTpValuesRef = useCommonCodeValues("FP_PROCESS_TIME_TYPE");
  const divideTpValuesRef = useCommonCodeValues("FP_DIVIDE_TP_CD");
  const latestReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_BOR"), viewName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);

  // routeCdNm/resourceCdNm은 AG Grid 외부 필터로 즉시 필터(§6-1 케이스3). itemCdNm은 서버 재조회
  // 예외(케이스2, fp-master-screen-guide.md §6-1 예외표) — retrieveBor의 서버 params로 전달.
  // rowData는 절대 새 배열로 갈아치우지 않는다(§6-1 — getRowId 없는 상태에서 rowData 참조가 바뀌면
  // grid row model이 재생성되어 미저장 신규 행이 조회조건 변경 시 사라지는 버그가 있었다).
  const doesExternalFilterPass = (node) => {
    if (!node.data?.id) return true;   // 미저장 신규 행은 조회조건과 무관하게 항상 표시(편집 중 초안)
    const { routeCdNm, resourceCdNm } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (routeCdNm && !(hit(r.routeCd, routeCdNm) || hit(r.routeNm, routeCdNm))) return false;
    if (resourceCdNm && !(hit(r.resourceCd, resourceCdNm) || hit(r.resourceNm, resourceCdNm))) return false;
    return true;
  };

  const isExternalFilterPresent = () => !!(searchFields.routeCdNm || searchFields.resourceCdNm);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  const columnDefs = [
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // 공정 그룹 — 코드/명칭은 항상 표시, 생산 품목(routeProdItems)만 펼쳤을 때 표시(old groupShowMode
    // "always"/"always"/"expand" 대응). 자원 그룹은 old도 expandable:false라 접기/펼침 없이 항상 표시.
    {
      headerName: "FP_ROUTE", groupId: "route", marryChildren: true, openByDefault: false,
      children: [
        // old buttonVisibleCallback: isNewGridButtonVisible 대응 — 신규 행에서만 재선택 가능(§3-3).
        { headerName: "FP_ROUTE_CD", field: "routeCd", colId: "routeCd", cellClass: "stringType", width: 180,
          editable: false, cellRenderer: CodePickerRenderer,
          cellRendererParams: { onOpenPopup: node => setRoutePopup({ open: true, type: "routeCd", node, routeCd: node.data?.routeCd }), hideButtonWhenSaved: true } },
        { headerName: "FP_ROUTE_NM", field: "routeNm", colId: "routeNm", type: ["nonEditableColumn"], width: 220 },
        // old routeProdItems button:"action" 대응 — 조회 버튼 클릭 시 같은 RoutePopup 인스턴스를
        // type="routeProdItems"로 열어 이 공정이 생산하는 품목 목록을 보여줌(선택 없음, 항상 클릭 가능 —
        // FK 재선택이 아니라 단순 참조 조회라 hideButtonWhenSaved 미적용).
        { headerName: "FP_ROUTE_PROD_ITEM", field: "routeProdItems", colId: "routeProdItems", type: ["nonEditableColumn"], width: 200, columnGroupShow: "open",
          cellRenderer: CodePickerRenderer,
          cellRendererParams: { onOpenPopup: node => setRoutePopup({ open: true, type: "routeProdItems", node: null, routeCd: node.data?.routeCd }) } }
      ]
    },
    {
      headerName: "FP_RESOURCE", groupId: "resource", marryChildren: true, openByDefault: true,
      children: [
        // old buttonVisibleCallback: isNewGridButtonVisible 대응 — 신규 행에서만 재선택 가능(§3-3).
        { headerName: "FP_RESOURCE_CD", field: "resourceCd", colId: "resourceCd", cellClass: "stringType", width: 180,
          editable: false, cellRenderer: CodePickerRenderer,
          cellRendererParams: { onOpenPopup: node => setResourcePopup({ open: true, node }), hideButtonWhenSaved: true } },
        { headerName: "FP_RESOURCE_NM", field: "resourceNm", colId: "resourceNm", type: ["nonEditableColumn"], width: 220 }
      ]
    },
    { headerName: "FP_DESC_TXT", field: "descTxt", colId: "descTxt", cellClass: "stringType", width: 130 },
    { headerName: "FP_ALT_RESOURCE_PRIORITY", field: "altResourcePriority", colId: "altResourcePriority", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_TIME_UOM", field: "timeUom", colId: "timeUom", width: 110, type: ["centerAligned"],
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: () => codeComboCellEditorParams("FP_TIME_UOM", timeUomValuesRef, t) },
    { headerName: "FP_QUEUE_TM", field: "queueTime", colId: "queueTime", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_SETUP_TM", field: "setupTime", colId: "setupTime", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_PROCESS_TM", field: "processTime", colId: "processTime", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_WAIT_TM", field: "waitTime", colId: "waitTime", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_MOVE_TM", field: "moveTime", colId: "moveTime", width: 110, type: ["numberColumn", "rightAligned"] },
    {
      headerName: "FP_LOT_SIZE", groupId: "lotSize", marryChildren: true, openByDefault: true,
      children: [
        { headerName: "FP_LOT_SIZE_MIN", field: "lotSizeMin", colId: "lotSizeMin", width: 110, type: ["numberColumn", "rightAligned"] },
        { headerName: "FP_LOT_SIZE_MAX", field: "lotSizeMax", colId: "lotSizeMax", width: 110, type: ["numberColumn", "rightAligned"] },
        { headerName: "FP_LOT_SIZE_MULTIPLR", field: "lotSizeMultiplr", colId: "lotSizeMultiplr", width: 110, type: ["numberColumn", "rightAligned"] }
      ]
    },
    { headerName: "FP_EFFICIENCY", field: "efficiency", colId: "efficiency", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_PROCESS_TM_TP_CD", field: "processTimeTpCd", colId: "processTimeTpCd", width: 110, type: ["centerAligned"],
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: () => codeComboCellEditorParams("FP_PROCESS_TIME_TYPE", processTimeTpValuesRef, t) },
    { headerName: "FP_STD_PROCESS_TM", field: "stdProcessTime", colId: "stdProcessTime", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_TRANSFER_BATCH_TM", field: "transferBatchTime", colId: "transferBatchTime", width: 130, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_DIVIDE_TP_CD", field: "divideTpCd", colId: "divideTpCd", width: 110, type: ["centerAligned"],
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: () => codeComboCellEditorParams("FP_DIVIDE_TP_CD", divideTpValuesRef, t) },
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    }
  ];

  const retrieveBor = () => {
    if (!planScope) return;
    const reqId = ++latestReqRef.current;
    gridRef.current?.api?.setGridOption("loading", true);

    fpBorService.getBors({ planScope, itemCdNm: searchFields.itemCdNm, isTool: false })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setBor([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setBor(results);
        }
      })
      .catch(e => debug(e))
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        DataState.initialize(gridRef.current?.api);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) {
          gridRef.current?.api?.setGridOption("loading", false);
        }
      });
  };

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    retrieveBor();
  };

  const insertRow = () => {
    const newRow = {
      id: null, planScope, routeCd: "", routeNm: "", resourceCd: "", resourceNm: "", descTxt: "",
      altResourcePriority: 1, timeUom: "SECOND", queueTime: 0, setupTime: 0, processTime: 0, waitTime: 0,
      moveTime: 0, lotSizeMin: 0, lotSizeMax: 0, lotSizeMultiplr: 0, efficiency: 1, processTimeTpCd: "N",
      stdProcessTime: 0, transferBatchTime: 0, divideTpCd: ""
    };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeBor = () => {
    gridRef.current.api.stopEditing();
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpBorService.removeBors(selectedRows)
        .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
        .catch(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }))
        .then(() => retrieveBor());
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveBor = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }

    fpBorService.upsertBors(allStateData)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch(e => { setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") }); debug(e); })
      .then(() => retrieveBor());
  };

  // old setRouteValues 대응 — routeCd/routeNm과 함께 선택 시점에 집계된 routeProdItems도 반영.
  const onSelectRoute = (row) => {
    const node = routePopup.node;
    if (node) {
      node.setDataValue("routeCd", row.routeCd);
      node.setDataValue("routeNm", row.routeNm);
      node.setDataValue("routeProdItems", row.routeProdItems);
    }
  };

  const onSelectResource = (row) => {
    const node = resourcePopup.node;
    if (node) {
      node.setDataValue("resourceCd", row.resourceCd);
      node.setDataValue("resourceNm", row.resourceNm);
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") saveBor();
    else if (action === "DELETE") removeBor();
  };

  // 화면 내부 탭(§11-3): planScope 또는 itemCdNm(서버 재조회 예외 필드) 변경 시에만 재조회.
  useEffect(() => {
    if (!gridRef.current?.api) return;
    retrieveBor();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope, searchFields.itemCdNm]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <DownloadButton onClick={() => gridRef.current.api.exportDataAsExcel({ fileName: `${t("FP_BOR")}.xlsx`, sheetName: t("FP_BOR") })} />
        <AddButton onClick={insertRow} />
        <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
        <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
      </Box>
      <Box id="fp-bor-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={bor}
          {...defaultGridMemo}
          rowSelection={{
            ...defaultGridMemo.rowSelection,
            headerCheckbox: true,
            checkboxes: true
          }}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onGridReady={onGridReady}
        />
      </Box>
      <RoutePopup
        open={routePopup.open}
        onClose={() => setRoutePopup({ open: false, type: "routeCd", node: null, routeCd: null })}
        planScope={planScope}
        type={routePopup.type}
        routeCd={routePopup.routeCd}
        onSelect={onSelectRoute}
      />
      <ResourcePopup
        open={resourcePopup.open}
        onClose={() => setResourcePopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectResource}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(BorTab);
